import type { Editor } from '@tiptap/react';
import type { Node as PMNode } from '@tiptap/pm/model';
import { lowlight } from './lowlight';

/** True when the selection sits inside a code block. */
export function isInCodeBlock(editor: Editor): boolean {
  return editor.isActive('codeBlock');
}

/**
 * The language highlight.js auto-detects for `text`, or null for empty text.
 * Used to show the user what an unset code block is being highlighted as —
 * mirrors CodeBlockLowlight's own `highlightAuto` fallback. Note: detection is
 * unreliable on short snippets (a few lines can misdetect), so this is a
 * best-effort display hint, not authoritative.
 */
export function detectCodeBlockLanguage(text: string): string | null {
  if (!text.trim()) return null;
  return lowlight.highlightAuto(text).data?.language ?? null;
}

/** The current code block's language, or null for plain text / no code block. */
export function getCodeBlockLanguage(editor: Editor): string | null {
  return (editor.getAttributes('codeBlock').language as string | null) ?? null;
}

/** The language name that marks a code block as a Mermaid diagram. */
export const MERMAID_LANGUAGE = 'mermaid';

/** True when `language` identifies a Mermaid diagram block (case-insensitive). */
export function isMermaidLanguage(language: string | null | undefined): boolean {
  return (language ?? '').toLowerCase() === MERMAID_LANGUAGE;
}

/**
 * Mermaid diagram-type keywords that open a diagram. Matched as a standalone
 * leading token on the first non-empty line — unambiguous enough to detect
 * mermaid by content with near-zero false positives (e.g. `graph TB`,
 * `sequenceDiagram`). NOT a fuzzy whole-body scan (no arrow-sniffing).
 */
const MERMAID_KEYWORDS = [
  'graph',
  'flowchart',
  'sequenceDiagram',
  'classDiagram',
  'stateDiagram-v2',
  'stateDiagram',
  'erDiagram',
  'journey',
  'gantt',
  'pie',
  'mindmap',
  'timeline',
  'gitGraph',
  'quadrantChart',
  'requirementDiagram',
  'C4Context',
  'sankey-beta',
  'xychart-beta',
  'block-beta',
];

/**
 * True if `code` looks like a Mermaid diagram — its first non-empty line starts
 * with a known diagram-type keyword (as a standalone token, so `graphqlQuery()`
 * does NOT match). Lets a pasted/typed mermaid block be recognized even without
 * an explicit `mermaid` language.
 */
export function looksLikeMermaid(code: string): boolean {
  const firstLine = code.split('\n').map((l) => l.trim()).find((l) => l.length > 0);
  if (!firstLine) return false;
  return MERMAID_KEYWORDS.some((kw) => {
    if (!firstLine.startsWith(kw)) return false;
    // Standalone token: end of string, or followed by a non-identifier char
    // (space, direction like TB/LR, punctuation) — not more letters/digits.
    const next = firstLine.charAt(kw.length);
    return next === '' || !/[A-Za-z0-9_]/.test(next);
  });
}

/**
 * Whether a code block should be treated as Mermaid: an explicit `mermaid`
 * language, OR mermaid-looking content when no explicit language is set. An
 * explicit non-mermaid language wins (the user chose it) — content detection
 * only fills in for unset/auto blocks.
 */
export function isMermaidBlock(language: string | null | undefined, code: string): boolean {
  if (isMermaidLanguage(language)) return true;
  if (language) return false; // explicit non-mermaid language — respect it
  return looksLikeMermaid(code);
}

export interface CodeBlockPosition {
  /** Document position OF the codeBlock node (doc.nodeAt(pos) is the block). */
  pos: number;
  /** The block's language attribute, or null for plain text. */
  language: string | null;
}

/**
 * Every code block in the document, with its position and language. Backs the
 * widget-decoration toolbar (one toolbar per code block, anchored by the editor
 * itself) — see code-block-toolbar-extension.ts.
 */
export function codeBlockPositions(doc: PMNode): CodeBlockPosition[] {
  const out: CodeBlockPosition[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name === 'codeBlock') {
      out.push({ pos, language: (node.attrs.language as string | null) ?? null });
      return false; // code blocks don't nest — no need to descend
    }
    return undefined;
  });
  return out;
}

export interface MermaidBlockPosition {
  /** Document position OF the codeBlock node. */
  pos: number;
  /** The mermaid source text of the block. */
  code: string;
}

/**
 * Every Mermaid code block in the document (language === 'mermaid'), with its
 * position and source text. Backs the mermaid diagram widget decoration (one
 * diagram per mermaid block) — see mermaid-extension.ts. Mirrors
 * `codeBlockPositions` but filtered to mermaid + carrying the block text.
 */
export function mermaidBlockPositions(doc: PMNode): MermaidBlockPosition[] {
  const out: MermaidBlockPosition[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name === 'codeBlock') {
      const code = node.textContent;
      if (isMermaidBlock(node.attrs.language as string | null, code)) {
        out.push({ pos, code });
      }
      return false; // code blocks don't nest
    }
    return undefined;
  });
  return out;
}

/**
 * Code-block actions targeting a SPECIFIC block by position — the widget
 * toolbar knows its own block's position, so actions operate on that block
 * regardless of where the selection is.
 *
 * Kept separate from the React component so the behavior is unit-testable
 * without mounting UI. The toolbar is a ProseMirror widget decoration (NOT a
 * NodeViewContent React node view): CodeBlockShiki highlights the block's
 * content via decorations, and wrapping that content in a node view caused a
 * flushSync-reentrancy freeze. A widget decoration is a sibling overlay the
 * editor positions — it never touches the content DOM Shiki owns.
 */
export const CODE_BLOCK_ACTIONS = {
  /** Set the language of the block at `pos` (or the selected block if omitted). */
  setLanguage: (editor: Editor, language: string | null, pos?: number) =>
    pos == null
      ? editor.chain().focus().updateAttributes('codeBlock', { language }).run()
      : editor
          .chain()
          .focus()
          .setNodeSelection(pos)
          .updateAttributes('codeBlock', { language })
          .run(),
  /** Copy the text of the block at `pos` (or the selected block if omitted). */
  copy: (editor: Editor, pos?: number) => {
    const node =
      pos == null
        ? editor.state.selection.$from.node(editor.state.selection.$from.depth)
        : editor.state.doc.nodeAt(pos);
    void navigator.clipboard.writeText(node?.textContent ?? '');
  },
  /** Delete the block at `pos` (or the selected block if omitted). */
  remove: (editor: Editor, pos?: number) =>
    pos == null
      ? editor.chain().focus().deleteNode('codeBlock').run()
      : editor.chain().focus().setNodeSelection(pos).deleteSelection().run(),
} as const;
